import { firebaseWebConfig, firebaseWebVapidKey } from '@/libs/firebase/config';
import { getFirebaseApp } from '@/libs/firebase/init.web';
import { captureException, warnLog } from '@/libs/logger';
import { deleteToken, getMessaging, getToken, isSupported, onMessage } from 'firebase/messaging';

import { removeDeviceToken, uploadDeviceToken } from './device-token-service';
import { routeNotification } from './notification-router';

const SERVICE_WORKER_PATH = '/firebase-messaging-sw.js';

/** True only in a browser that can actually receive FCM web push. */
export async function isWebPushSupported(): Promise<boolean> {
  try {
    if (typeof window === 'undefined') return false;
    if (!('serviceWorker' in navigator) || !('Notification' in window)) return false;
    return await isSupported();
  } catch {
    return false;
  }
}

// The SW can't read build-time env, so the (public) Firebase config rides in as
// query params; the worker parses them in `firebase.initializeApp`.
function buildServiceWorkerUrl(): string {
  const params = new URLSearchParams();
  const cfg = firebaseWebConfig as Record<string, string | undefined>;
  ['apiKey', 'authDomain', 'projectId', 'storageBucket', 'messagingSenderId', 'appId'].forEach(
    key => {
      const value = cfg[key];
      if (value) params.set(key, value);
    },
  );
  return `${SERVICE_WORKER_PATH}?${params.toString()}`;
}

async function ensurePermission(): Promise<boolean> {
  if (Notification.permission === 'granted') return true;
  if (Notification.permission === 'denied') return false;
  const result = await Notification.requestPermission();
  return result === 'granted';
}

/**
 * Requests permission, registers the FCM service worker, obtains a web push
 * token and uploads it (same `/me/device-tokens` endpoint as native), then wires
 * foreground display + click routing. Returns a cleanup function, or `null` when
 * web push is unavailable/declined. Best-effort: never throws to the caller.
 */
export async function registerWebPush(): Promise<(() => void) | null> {
  try {
    if (!(await isWebPushSupported())) return null;
    if (!firebaseWebVapidKey) {
      warnLog('[web-push] EXPO_PUBLIC_FIREBASE_VAPID_KEY missing — web push disabled');
      return null;
    }
    if (!(await ensurePermission())) return null;

    const registration = await navigator.serviceWorker.register(buildServiceWorkerUrl());
    const messaging = getMessaging(getFirebaseApp());
    const token = await getToken(messaging, {
      vapidKey: firebaseWebVapidKey,
      serviceWorkerRegistration: registration,
    });
    if (!token) return null;

    await uploadDeviceToken(token);

    // Foreground: FCM does not auto-display, so post it ourselves via the SW.
    const unsubscribeMessage = onMessage(messaging, payload => {
      const notification = payload.notification ?? {};
      const data = (payload.data ?? {}) as Record<string, string>;
      const title = notification.title ?? data.title ?? 'U.S. Kids Golf';
      registration.showNotification(title, {
        body: notification.body ?? data.body ?? '',
        icon: notification.icon ?? '/favicon.ico',
        data,
      });
    });

    // Clicks are handled in the SW, which forwards the data payload to the page.
    const onSwMessage = (event: MessageEvent) => {
      if (event.data?.type === 'PUSH_NOTIFICATION_CLICK') {
        routeNotification(event.data.data);
      }
    };
    navigator.serviceWorker.addEventListener('message', onSwMessage);

    return () => {
      unsubscribeMessage();
      navigator.serviceWorker.removeEventListener('message', onSwMessage);
    };
  } catch (e) {
    captureException({ error_payload: e }, { tags: { usk_log_type: 'web_push_register_error' } });
    return null;
  }
}

/**
 * Drops the web push token server-side and locally. Call at start of sign-out.
 *
 * `authToken` is captured by the caller before sign-out and pinned onto the
 * DELETE, so it succeeds even if this runs past the sign-out race (see
 * `removeDeviceToken`).
 */
export async function unregisterWebPush(authToken?: string): Promise<void> {
  try {
    if (!(await isWebPushSupported()) || !firebaseWebVapidKey) return;
    const registration = await navigator.serviceWorker.getRegistration(SERVICE_WORKER_PATH);
    const messaging = getMessaging(getFirebaseApp());
    const token = await getToken(messaging, {
      vapidKey: firebaseWebVapidKey,
      serviceWorkerRegistration: registration ?? undefined,
    });
    if (token) await removeDeviceToken(token, authToken);
    await deleteToken(messaging);
  } catch (e) {
    captureException({ error_payload: e }, { tags: { usk_log_type: 'web_push_unregister_error' } });
  }
}
